// This will be the scaffold for a modern, multi-step project upload wizard
// Step 1: Project Info
// Step 2: Design Files
// Step 3: Reports
// Step 4: Legal Docs
// Step 5: Review & Submit

import React, { useState } from 'react';
import { Upload, Trash, Loader, X } from 'lucide-react';
import api from '../../axios';

const initialDocuments = {
  architectural_design: null,
  structural_design: null,
  electrical_design: null,
  mechanical_design: null,
  soil_report: null,
  eia_report: null,
  site_analysis_report: null,
  title_document: null,
  affidavit: null,
  power_of_attorney: null,
  aprn_certificate: null,
};

const ProjectUploadWizard = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [projectData, setProjectData] = useState({
    name: '',
    description: '',
    location: '',
    documents: { ...initialDocuments },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleInputChange = (e) => {
    setProjectData({ ...projectData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e, key) => {
    const file = e.target.files[0];
    setProjectData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        [key]: file,
      },
    }));
  };

  const renderUploadField = (label, key) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="file"
        accept=".pdf,.ifc"
        onChange={(e) => handleFileChange(e, key)}
        className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50"
      />
      {projectData.documents[key] && (
        <div className="mt-2 text-sm text-green-600">Selected: {projectData.documents[key].name}</div>
      )}
    </div>
  );

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <h2 className="text-xl font-semibold mb-4">Project Info</h2>
            <input type="text" name="name" placeholder="Project Name" value={projectData.name} onChange={handleInputChange} className="w-full mb-3 p-2 border rounded" />
            <textarea name="description" placeholder="Description" value={projectData.description} onChange={handleInputChange} className="w-full mb-3 p-2 border rounded"></textarea>
            <input type="text" name="location" placeholder="Location" value={projectData.location} onChange={handleInputChange} className="w-full mb-3 p-2 border rounded" />
          </>
        );
      case 2:
        return (
          <>
            <h2 className="text-xl font-semibold mb-4">Design Documents</h2>
            {renderUploadField("Architectural Design", "architectural_design")}
            {renderUploadField("Structural Design", "structural_design")}
            {renderUploadField("Electrical Design", "electrical_design")}
            {renderUploadField("Mechanical Design", "mechanical_design")}
          </>
        );
      case 3:
        return (
          <>
            <h2 className="text-xl font-semibold mb-4">Reports</h2>
            {renderUploadField("Soil Test Report", "soil_report")}
            {renderUploadField("Environmental Impact Assessment (EIA)", "eia_report")}
            {renderUploadField("Site Analysis Report (SAR)", "site_analysis_report")}
          </>
        );
      case 4:
        return (
          <>
            <h2 className="text-xl font-semibold mb-4">Ownership & Legal Documents</h2>
            {renderUploadField("Title Document (C of O / R of O)", "title_document")}
            {renderUploadField("Affidavit of Ownership", "affidavit")}
            {renderUploadField("Registered Power of Attorney", "power_of_attorney")}
            {renderUploadField("APRN Certificate", "aprn_certificate")}
          </>
        );
      case 5:
        return (
          <>
            <h2 className="text-xl font-semibold mb-4">Review & Submit</h2>
            <p className="mb-2">Please review your files before submitting.</p>
            <ul className="text-sm text-gray-700 list-disc pl-5">
              {Object.entries(projectData.documents).map(([key, file]) => (
                file && <li key={key}>{key.replace(/_/g, ' ')}: {file.name}</li>
              ))}
            </ul>
          </>
        );
      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('name', projectData.name);
    formData.append('description', projectData.description);
    formData.append('location', projectData.location);
    for (const key in projectData.documents) {
      if (projectData.documents[key]) {
        formData.append(`documents[${key}]`, projectData.documents[key]);
      }
    }
    // TODO: Axios POST call with progress tracking here
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl p-6 relative max-h-screen overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700">
          <X className="h-6 w-6" />
        </button>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-4">
            <span className="text-gray-600">Step {step} of 5</span>
            <div className="space-x-2">
              {step > 1 && <button onClick={() => setStep(step - 1)} className="px-4 py-1 bg-gray-200 rounded">Back</button>}
              {step < 5 && <button onClick={() => setStep(step + 1)} className="px-4 py-1 bg-blue-600 text-white rounded">Next</button>}
              {step === 5 && <button onClick={handleSubmit} disabled={isSubmitting} className="px-4 py-1 bg-green-600 text-white rounded">{isSubmitting ? 'Submitting...' : 'Submit'}</button>}
            </div>
          </div>
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};

export default ProjectUploadWizard;
